import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { Card, Row, Col, Result, Space, Table, Tag } from 'antd';
import { useNavigate } from "react-router-dom";
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import { VerifiedRounded, WarningRounded } from "@mui/icons-material";
import { stringMixin } from "../../utils/strings";
import { useTickerSearchIndex } from "../../modules/Search/useTickerSearchIndex";
import TickerChip from "../../components/TickerChip";
import {setNewPortfolioBuilderContext} from "../../store/slices/builder.slice";
// Parent Component
const BuilderUploadInstance = ({ filename }) => {
    const fileUploadResults = useSelector(state => state.builder.fileUploads?.[filename]);
    const fileData = fileUploadResults?.data ?? [];
    const portfoliosInResult = fileData.length;

    const uniqueTickers = fileData && fileData.reduce((acc, portfolio) => {
        portfolio.forEach((asset) => {
            if (!acc.includes(asset.symbol)) {
                acc.push(asset.symbol);
            }
        });
        return acc;
    }, []);

    const { tickers } = useTickerSearchIndex(uniqueTickers);

    return (
        <Row justify={'center'} align={'middle'}>
            <Col span={8}>
                <Result
                    status="success"
                    title="Portfolio Upload Results"
                    subTitle={`Unique portfolios detected: (${portfoliosInResult})`}
                />
            </Col>
            <Col span={16}>
                <Card bordered={false}>
                    <Space direction={'vertical'} style={{height: 600, overflowY: 'auto'}}>
                        {fileData.length > 0 && fileData.map((portfolio, index) => (
                                <UploadedPortfolio portfolio={portfolio} tickers={tickers} index={index+1}/>
                        ))}
                    </Space>
                </Card>
            </Col>
        </Row>
    );
};

// Child Component
const UploadedPortfolio = ({ portfolio, tickers, index }) => {
    // State to track if the portfolio is fully verified
    const [isVerified, setIsVerified] = useState(false);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        const verified = portfolio.every(asset => {
            const ticker = tickers.find(x => x.symbol === asset.symbol);
            return ticker && ticker.valid;
        });
        setIsVerified(verified);
    }, [portfolio, tickers]);

    const handleCreate = () => {
        const formattedPortfolio = portfolio.map(asset => {
            const ticker = tickers.find(x => x.symbol === asset.symbol);
            return {
                id: asset.symbol,
                name: ticker?.name,
                wgt: asset.weight * 100,
                type: ticker ? ticker.type : 'unknown'
            };
        });
        dispatch(setNewPortfolioBuilderContext({ type: 'm', items: formattedPortfolio }));
        navigate('/builders/portfolio');

        // Execute onCreate callback
        // onCreate();
    };

    const columns = [
        {
            title: "Verified",
            dataIndex: "valid",
            key: "valid",
            render: (text, record) => record.valid === true ? <VerifiedRounded style={{ color: 'green' }} /> : <WarningRounded style={{ color: 'red' }} />
        },
        {
            title: "Symbol",
            dataIndex: "symbol",
            key: "symbol",
            render: (text) => {
                const ticker = tickers.find(x => x.symbol === text);
                return ticker ? <TickerChip ticker={ticker} /> : text;
            }
        },
        {
            title: "Weight",
            dataIndex: "weight",
            key: "weight",
            render: (text) => stringMixin.pct(text, 2)
        }
    ];

    return (
        <Card bordered={false}
              title={
                <ButtonGroup>
                    <Button
                        variant={'contained'}
                        color={'info'}
                        onClick={handleCreate}
                    >
                        Create
                    </Button>
                </ButtonGroup>
              }
              extra={<Tag bordered={false}>Portfolio {index}</Tag>}

        >
            <Table columns={columns} dataSource={[...portfolio].sort((a, b)=>b.weight-a.weight)} />
        </Card>
    );
};

export default BuilderUploadInstance;
